/**
 * Single source of truth for Mantine color system constants.
 *
 * All color-related logic should import from this module.
 */

/**
 * Available Mantine theme color names.
 */
export const MantineThemeColors = [
  'blue',
  'red',
  'green',
  'pink',
  'grape',
  'violet',
  'indigo',
  'cyan',
  'teal',
  'lime',
  'yellow',
  'orange',
] as const;

export type MantineColor = (typeof MantineThemeColors)[number];

/**
 * Default shade used for chart series and other auto-colored elements.
 * Shade 6 is the primary shade in Mantine's color system.
 */
export const DEFAULT_COLOR_SHADE = 6;

/**
 * Ordered color palette for chart series - cycles for unlimited series.
 * Uses base color names; shade is applied via formatMantineColor().
 */
export const SERIES_COLORS: MantineColor[] = [
  'blue',
  'green',
  'yellow',
  'red',
  'grape',
  'teal',
  'lime',
  'orange',
  'pink',
];

/**
 * Formats a color name and optional shade into Mantine's color notation.
 *
 * @param color - Base color name (e.g., 'blue', 'red')
 * @param shade - Optional shade value 0-9 (default: DEFAULT_COLOR_SHADE)
 * @returns Formatted color string (e.g., 'blue.6')
 *
 * @example
 * formatMantineColor('blue')     // 'blue.6'
 * formatMantineColor('red', 3)   // 'red.3'
 */
export function formatMantineColor(color: string, shade: number = DEFAULT_COLOR_SHADE): string {
  return `${color}.${shade}`;
}

/**
 * Gets the auto-assigned series color for a given index.
 * Cycles through SERIES_COLORS palette for unlimited series.
 *
 * @param index - Series index (0-based)
 * @returns Formatted color string (e.g., 'blue.6')
 *
 * @example
 * getSeriesColor(0) // 'blue.6'
 * getSeriesColor(1) // 'green.6'
 */
export function getSeriesColor(index: number): string {
  const color = SERIES_COLORS[index % SERIES_COLORS.length] ?? 'blue';
  return formatMantineColor(color, DEFAULT_COLOR_SHADE);
}

/**
 * Resolves a series color to Mantine's color.shade notation.
 * Uses the series's explicit color/shade, or falls back to auto-assigned color with default shade.
 *
 * @param series - Object with optional color and shade properties
 * @param index - Series index for auto-color assignment
 * @returns Formatted color string (e.g., 'blue.6')
 */
export function resolveSeriesColor(series: { color?: string; shade?: number }, index: number): string {
  if (!series.color && series.shade === undefined) {
    return getSeriesColor(index);
  }
  const fallbackColor = SERIES_COLORS[index % SERIES_COLORS.length] ?? 'blue';
  const color = series.color ?? fallbackColor;
  const shade = series.shade ?? DEFAULT_COLOR_SHADE;
  return formatMantineColor(color, shade);
}
